
import { Suspense } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ForProviders = () => {
  return (
    <div className="min-h-screen bg-white">
      <Suspense fallback={<div className="h-20 bg-white"></div>}>
        <Navbar />
      </Suspense>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-gray-100 pt-20 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              For Clinics & Facilities
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Flexible implementation models for infrared recovery technology across 
              physical therapy clinics, orthopedic practices, performance facilities, and recovery studios.
            </p>
          </div>
        </div>
      </section>

      {/* Physical Therapy Clinics */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Physical Therapy Clinics</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Workflow Integration</h3>
                  <p className="text-gray-600 mb-6">
                    Seamlessly integrate 10-20 minute sessions before or after traditional PT sessions. 
                    Non-invasive adjunct support that complements your existing treatment protocols without 
                    disrupting established workflows.
                  </p>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Patient Experience</h3>
                  <p className="text-gray-600">
                    Comfortable supine positioning during recovery sessions. Patients appreciate the 
                    relaxing, warm experience that may support their overall recovery journey alongside 
                    standard care protocols.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Cash-Pay Add-On Options</h3>
                  <div className="bg-slate-50 p-6 rounded-lg">
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-teal-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span><strong>Per-session add-on:</strong> Individual session upgrades</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-teal-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span><strong>10-session bundles:</strong> Package pricing for committed patients</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-teal-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span><strong>Monthly recovery upgrade:</strong> Subscription-style add-on service</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Orthopedic Practices */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12">Orthopedic Practices</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-sm">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Referral Model</h3>
                <p className="text-gray-600 mb-4">
                  Partner with local recovery facilities or PT clinics that offer infrared recovery services. 
                  Refer post-operative patients for adjunct recovery support during their rehabilitation phase.
                </p>
                <ul className="text-gray-600 space-y-2">
                  <li>• Expand patient care options without equipment investment</li>
                  <li>• Maintain relationships with trusted partner facilities</li>
                  <li>• Track patient outcomes through coordinated care</li>
                </ul>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-sm">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">In-Clinic Model</h3>
                <p className="text-gray-600 mb-4">
                  Install systems within your practice for direct patient access to adjunct recovery services. 
                  Operational simplicity with minimal staff training requirements.
                </p>
                <ul className="text-gray-600 space-y-2">
                  <li>• Enhanced patient experience within your facility</li>
                  <li>• Additional revenue stream opportunity</li>
                  <li>• Streamlined scheduling and care coordination</li>
                </ul>
              </div>
            </div>
            <div className="mt-8 p-6 bg-blue-50 rounded-lg">
              <p className="text-gray-700 text-center">
                <strong>Focus:</strong> Patient experience enhancement and operational simplicity. 
                No medical claims — positioned as adjunct support alongside standard post-operative care protocols.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gyms & Performance Facilities */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Gyms & Performance Facilities</h2>
            <div className="bg-gradient-to-r from-teal-50 to-green-50 p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Membership Upgrade Model</h3>
              <p className="text-gray-600 mb-6">
                Position infrared recovery as a premium membership add-on. Small monthly fee addition 
                to existing dues structure, creating new revenue streams while enhancing member retention 
                and satisfaction.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-user-add-line text-white text-xl"></i>
                  </div>
                  <h4 className="font-semibold text-gray-800">Member Attraction</h4>
                  <p className="text-sm text-gray-600">Premium recovery amenity</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-repeat-line text-white text-xl"></i>
                  </div>
                  <h4 className="font-semibold text-gray-800">Retention Boost</h4>
                  <p className="text-sm text-gray-600">Enhanced member experience</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-money-dollar-circle-line text-white text-xl"></i>
                  </div>
                  <h4 className="font-semibold text-gray-800">Revenue Growth</h4>
                  <p className="text-sm text-gray-600">Additional monthly income</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recovery Studios */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Recovery Studios & Multi-Room Operators</h2>
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Scaling Infrastructure</h3>
                <p className="text-gray-600 mb-4">
                  Design dedicated recovery environments with multiple bed configurations. 
                  Optimize room layouts for efficient patient flow and operational management.
                </p>
                <ul className="text-gray-600 space-y-2">
                  <li>• Multiple bed rooms for simultaneous sessions</li>
                  <li>• Standardized protocols across locations</li>
                  <li>• Efficient scheduling and booking systems</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Multi-Location Strategy</h3>
                <p className="text-gray-600 mb-4">
                  Consistent equipment and protocols across multiple facilities. 
                  Streamlined operations and staff training for franchise or multi-location models.
                </p>
                <ul className="text-gray-600 space-y-2">
                  <li>• Standardized equipment packages</li>
                  <li>• Unified training and protocols</li>
                  <li>• Coordinated service and support</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Which Model Fits You */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Which Model Fits You?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center bg-slate-50 p-8 rounded-lg">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-play-line text-white text-2xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Start: 1 Bed</h3>
                <p className="text-gray-600 mb-4">Pilot Program</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Test patient response</li>
                  <li>• Minimal initial investment</li>
                  <li>• Learn operational flow</li>
                  <li>• Build staff confidence</li>
                </ul>
              </div>
              <div className="text-center bg-slate-50 p-8 rounded-lg">
                <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-arrow-up-line text-white text-2xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Grow: 3 Beds</h3>
                <p className="text-gray-600 mb-4">Dedicated Recovery Room</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Dedicated space design</li>
                  <li>• Higher patient volume</li>
                  <li>• Established protocols</li>
                  <li>• Revenue optimization</li>
                </ul>
              </div>
              <div className="text-center bg-slate-50 p-8 rounded-lg">
                <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-building-line text-white text-2xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Scale: 5+ Beds</h3>
                <p className="text-gray-600 mb-4">Multi-Room or Multi-Location</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Multiple treatment rooms</li>
                  <li>• Franchise opportunities</li>
                  <li>• Operational expertise</li>
                  <li>• Market leadership</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Procurement Ready */}
      <section className="py-16 bg-teal-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Procurement-Ready Support</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Documentation & Compliance</h3>
                <ul className="text-gray-600 space-y-2 text-left">
                  <li>• Documentation available upon request</li>
                  <li>• Compliance packet preparation</li>
                  <li>• Facility implementation guidelines</li>
                  <li>• Staff training materials</li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">U.S.-Based Support</h3>
                <ul className="text-gray-600 space-y-2 text-left">
                  <li>• U.S. time-zone support team</li>
                  <li>• Logistics coordination</li>
                  <li>• Service and parts support</li>
                  <li>• Installation coordination</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Explore Implementation?</h2>
            <p className="text-xl text-gray-300 mb-8">
              Connect with our specialists to discuss which model best fits your facility's needs and patient population.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap">
                Request a Quote
              </button>
              <button className="bg-transparent border-2 border-teal-500 text-teal-500 hover:bg-teal-500 hover:text-white px-8 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap">
                Talk to a Specialist
              </button>
              <button className="bg-transparent border border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white px-8 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap">
                Request Compliance Packet
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ForProviders;
